import type { ChatSuggestion } from './chat-types';
import { ACCENT_TEXT } from '../AuthControls';

type ChatSuggestionsProps = {
  suggestions: readonly ChatSuggestion[];
  /** Inset from the right edge. The login panel insets; the full screen signup does not. */
  inset?: boolean;
  /**
   * Keep the row's height while it is empty, so the thread above does not jump each
   * time Pertento starts and stops speaking. Off where the row is not always coming
   * back, e.g. once signup has moved past the pitch.
   */
  reserve?: boolean;
};

/**
 * Rendered inside the thread, straight after the last message, rather than pinned to
 * the bottom of the panel. Pinned, they sat a long way from the thing they answer and
 * were easy to miss; here they read as the next line of the conversation.
 *
 * Right aligned, like the visitor's own bubbles, because tapping one says it for
 * them. Filled white with a full-strength accent border, so they read as buttons on
 * the tinted panel: a hairline border on a transparent pill was too faint to see as
 * something to press.
 */
export const ChatSuggestions = ({ suggestions, inset = false, reserve = true }: ChatSuggestionsProps) => {
  if (!reserve && suggestions.length === 0) return null;

  return (
    <div
      className={`mt-[18px] flex flex-col items-end gap-2 ${reserve ? 'min-h-[76px]' : ''}`}
      style={inset ? { paddingRight: 'clamp(0px, 8%, 56px)' } : undefined}
    >
      {suggestions.map((suggestion) => (
        <button
          key={suggestion.label}
          type="button"
          onClick={suggestion.onPick}
          className={`pt-msg-in bg-panel ${ACCENT_TEXT} border-accent hover:bg-accent h-9 flex-none rounded-full border px-4 text-body leading-none font-medium whitespace-nowrap transition-colors duration-150 hover:text-white dark:hover:text-white`}
        >
          {suggestion.label}
        </button>
      ))}
    </div>
  );
};
